import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import queryString from "query-string";

import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";

import api from "libs/api";

const WebmasterContext = createContext();

const WebmasterProvider = ({ children }) => {
  const { id } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { page: _page, ...filters } = queryString.parse(search);
  const default_sort = "created_at:desc";

  const [init_loading, setInitLoading] = useState(true);

  const {
    isLoading: loading,
    data: webmaster,
    isSuccess,
  } = useQuery({
    queryKey: ["webmaster", id, filters],
    queryFn: () =>
      api.get(
        `/admin/webmasters/${id}/tasks?${queryString.stringify({
          status:
            task_status_data
              ?.filter(
                (item) =>
                  filters?.status?.split(",").includes(item?.key) &&
                  item?.key !== "canceled",
              )
              .map((item) => item?.value)
              .join(",") || undefined,
          priority:
            task_priority_data
              ?.filter((item) =>
                filters?.priority?.split(",").includes(item?.key),
              )
              .map((item) => item?.value)
              .join(",") || undefined,
          sort: filters?.sort || default_sort,
        })}`,
      ),
    select: (webmaster) => ({
      ...webmaster,
      tasks: webmaster?.tasks?.filter((task) => task.status !== 7),
    }),
  });

  useEffect(() => {
    if (isSuccess) {
      setInitLoading(false);
    }
  }, [isSuccess]);

  const filterClearAll = () => {
    const query = filters?.sort
      ? `?${queryString.stringify({ sort: filters.sort })}`
      : "";

    navigate(`/webmasters/${id}${query}`);
  };

  const applyFilters = (newFilters) => {
    const merged = { ...filters };

    Object.entries(newFilters).forEach(([name, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === false ||
        value === ""
      ) {
        delete merged[name];
      } else {
        merged[name] = typeof value === "boolean" ? value : value?.key;
      }
    });

    navigate(`/webmasters/${id}?${queryString.stringify(merged)}`);
  };

  const filterHandler = (name, value, accumulate = false) => {
    let accumulated_value = value;

    if (accumulate) {
      const currentValues = filters[name] ? filters[name].split(",") : [];

      if (value) {
        if (currentValues.includes(value)) {
          const filteredValues = currentValues.filter((val) => val !== value);
          accumulated_value =
            filteredValues.length > 0 ? filteredValues.join(",") : undefined;
        } else {
          currentValues.push(value);
          accumulated_value = currentValues.join(",");
        }
      } else {
        accumulated_value =
          currentValues.length > 0 ? currentValues.join(",") : undefined;
      }
    }

    navigate(
      `/webmasters/${id}?${queryString.stringify({
        ...filters,
        [name]: accumulated_value || undefined,
      })}`,
    );
  };

  const updateTask = (_id, values) =>
    new Promise(async (resolve, reject) => {
      try {
        const updated = await api.put(`/admin/tasks/${_id}`, values);
        const { _id: updated_id, priority, status } = updated;

        queryClient.setQueryData(["webmaster", id, filters], (prev) => ({
          ...prev,
          tasks: prev.tasks.map((task) =>
            task._id === updated_id ? { ...task, priority, status } : task,
          ),
        }));

        queryClient.invalidateQueries(["webmaster", id, filters]);

        resolve();
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong.");
        reject(err);
      }
    });

  const value = useMemo(() => {
    return {
      data: webmaster,
      webmaster_id: id,
      loading,
      init_loading,
      filters,
      filterHandler,
      filterClearAll,
      applyFilters,
      updateTask,
    };
    // eslint-disable-next-line
  }, [webmaster, loading, init_loading, filters]);

  return (
    <WebmasterContext.Provider value={value}>
      {children}
    </WebmasterContext.Provider>
  );
};

const useWebmaster = () => useContext(WebmasterContext);
export { WebmasterContext, useWebmaster };
export default WebmasterProvider;
